// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useId } from 'react';
import './ToggleSwitch.css';

export interface ToggleSwitchProps {
  /** Whether the switch is on. */
  checked: boolean;
  /** Called with the next checked state when the user toggles. */
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Accessible label (rendered as aria-label on the switch button). */
  'aria-label'?: string;
  className?: string;
  id?: string;
}

/**
 * Finerty design-system toggle switch. Renders the on/off pill from the design
 * spec (purple `#2D0A6C` when on with the knob on the right; grey `#A3A3A3` when
 * off with the knob on the left). Implemented as a `role="switch"` button so it
 * is keyboard- and screen-reader-accessible.
 */
export function ToggleSwitch({
  checked,
  onChange,
  disabled = false,
  className = '',
  id: idProp,
  ...rest
}: ToggleSwitchProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const ariaLabel = rest['aria-label'];

  const classes = [
    'fin-toggle',
    checked && 'fin-toggle--on',
    disabled && 'fin-toggle--disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      className={classes}
      onClick={() => onChange(!checked)}
    >
      <svg
        className="fin-toggle__svg"
        width="48"
        height="24"
        viewBox="0 0 48 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <rect
          className="fin-toggle__track"
          width="48"
          height="24"
          rx="12"
        />
        <rect
          className="fin-toggle__knob"
          x={checked ? 27.4834 : 3}
          y="3"
          width="18"
          height="18"
          rx="9"
          fill="white"
        />
      </svg>
    </button>
  );
}
